import React from "react";
import { Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { WarningRounded } from "@mui/icons-material";
import { LOCATION_STATUS_STYLES } from "../../../types/LocationStatus";
import { useLocationCardContext } from "./LocationCardContext";
import ImageComponent from "../../skeleton/ImageComponent";
import { constructListingImageUrl } from "../../../util/ConstructImageUrlUtil";

interface LocationCardImageProps {
  /** `listingImageFileName` from `ILocationData`. Pass `undefined` to show the placeholder. */
  fileName?: string | null;
  /** Accessible alt text for the image. Defaults to "Location image". */
  alt?: string;
}

const ImageRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: 160,
  flexShrink: 0,
  background: "#d4dde6",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
  // SM+: fixed-width left sidebar
  [theme.breakpoints.up("sm")]: {
    width: 130,
    minHeight: 130,
  },
}));

const StyledImage = styled(ImageComponent)({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  position: "absolute",
  top: 0,
  left: 0,
});

const PlaceholderLabel = styled(Box)({
  fontSize: "0.6429rem", // 9px at 14px root
  letterSpacing: "0.5px",
  color: "#8a9aaa",
  textAlign: "center",
  textTransform: "uppercase",
  padding: "4px",
  lineHeight: 1.4,
  marginTop: 6,
});

const StatusRibbon = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerBg",
})<{ ownerBg: string }>( ({ ownerBg }) => ({
  position: "absolute",
  top: 10,
  left: 0,
  padding: "3px 10px 3px 8px",
  fontSize: "0.7143rem", // 10px at 14px root
  fontWeight: 700,
  letterSpacing: "0.5px",
  borderRadius: "0 4px 4px 0",
  color: "#fff",
  textTransform: "uppercase",
  background: ownerBg,
  zIndex: 1,
}));

const STATUS_RIBBON_LABEL: Record<string, string> = {
  draft: "Draft",
  pending: "Pending",
  active: "Active",
  deactivated: "Deactivated",
};

/**
 * Image sidebar / header for a `LocationCard`.
 *
 * - Shows a status ribbon in the top-left corner colored via `LOCATION_STATUS_STYLES`.
 * - Falls back to a placeholder icon when no `fileName` is provided.
 * - On mobile: full-width, fixed height. On SM+: fixed-width left sidebar.
 * - Reads `derivedStatus` from the nearest `LocationCard` context (no prop needed).
 */
const LocationCardImage = ({ fileName, alt = "Location image" }: LocationCardImageProps) => {
  const { derivedStatus } = useLocationCardContext();
  const tokens = LOCATION_STATUS_STYLES[derivedStatus];

  return (
    <ImageRoot>
      {fileName ? (
        <StyledImage
          src={constructListingImageUrl(fileName)}
          alt={alt}
        />
      ) : (
        <>
          <WarningRounded sx={{ fontSize: "2rem", color: "#aabbc8" }} aria-hidden="true" />
          <PlaceholderLabel>No Image<br />Available</PlaceholderLabel>
        </>
      )}
      <StatusRibbon ownerBg={tokens.color} aria-label={`Status: ${STATUS_RIBBON_LABEL[derivedStatus]}`}>
        {STATUS_RIBBON_LABEL[derivedStatus]}
      </StatusRibbon>
    </ImageRoot>
  );
};

export default LocationCardImage;

